/**
 * ClientsListView Component (TASK-337, was the body of ClientsPage / TASK-137)
 *
 * The "Контакты" tab of the Clients section: clients list with search,
 * create dialog, and client actions. Extracted as-is from ClientsPage when
 * the section got URL-driven tabs (FR-001); the private useDebounce and
 * pagination copies were replaced by the shared modules (AC-5).
 */
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  UserPlus,
  Search,
  Loader2,
  Users,
  AlertCircle,
  Phone,
  MessageCircle,
  Mail,
  FileText,
  Trash2,
} from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { PaginationBar } from '@/components/common/PaginationBar';
import { usePageSize } from '@/hooks/usePageSize';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FollowupBadge } from '@/components/funnel/FunnelBadges';
import { ClientsFilters } from '@/components/clients/ClientsFilters';
import { FilterField } from '@/components/clients/FilterField';
import { useClientListFilters } from '@/components/clients/useClientListFilters';
import { useDropStaleFilterValues } from '@/components/clients/useDropStaleFilterValues';
import { useLeadAttributes } from '@/hooks/useLeadAttributes';
import {
  ClientMessagesCount,
  ClientFunnelStage,
  ClientManagerName,
} from '@/components/clients/ClientListCells';
import { ClientSenderAccount } from '@/components/clients/ClientSenderAccount';
import { OwnContactBadge } from '@/components/clients/OwnContactSwitch';
import { useClientTranscript } from '@/components/clients/useClientTranscript';
import { TelegramUsernameLink } from '@/components/clients/TelegramContacts';
import { useDebounce } from '@/hooks/useDebounce';
import { clientsApi } from '@/services/api/clients';
import { formatClientActivity, formatClientPhone } from '@/lib/clientListFormat';
import type { Client, ClientFilters, CreateClientPayload } from '@/types/client';
import { useCanCallApi } from '@/hooks/useApiAccess';
import { ANY_UUID } from '@/lib/managerAccess';

/** KAN-646: compact cells so the table fits 1440px without horizontal scroll. */
const HEAD = 'h-10 px-2 whitespace-nowrap';
const CELL = 'px-2 py-2';
/** Narrow column: as wide as its content, never wraps. */
const NARROW = 'w-px whitespace-nowrap';

interface ClientsListViewProps {
  /** Tabs bar of the Clients section container, rendered above the content. */
  tabsBar?: React.ReactNode;
}

export function ClientsListView({ tabsBar }: ClientsListViewProps): JSX.Element {
  const queryClient = useQueryClient();
  // KAN-811: adding and deleting clients are closed to the MANAGER role.
  const canCreate = useCanCallApi('POST', '/api/v1/clients/');
  const canDelete = useCanCallApi('DELETE', `/api/v1/clients/${ANY_UUID}/`);
  const navigate = useNavigate();

  // State
  const { page: currentPage, setPage: setCurrentPage, pageSize, setPageSize } = usePageSize('clients');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);

  const resetPage = useCallback(() => setCurrentPage(1), [setCurrentPage]);
  // Dashboard funnel card links here with ?funnel=&stage= (wins over remembered
  // filters); KAN-682: ?source= presets the "Источник" filter the same way;
  // KAN-734: ?worked=1 (link from the dashboard "Отработанные") presets "Отработанные".
  // KAN-737: &outcome= (dashboard funnel segment) presets the "Исход" filter next to the stage.
  // KAN-744: ?manager=<id>|none (manager card) presets the "Менеджер" filter.
  const [searchParams, setSearchParams] = useSearchParams();
  const [stagePreset] = useState(() => {
    const stage = searchParams.get('stage');
    const source = searchParams.get('source');
    const worked = searchParams.get('worked') === '1';
    const manager = searchParams.get('manager');
    if (!stage && !source && !worked && !manager) return null;
    return {
      stage,
      funnel: searchParams.get('funnel'),
      source,
      worked,
      outcome: searchParams.get('outcome'),
      manager,
    };
  });
  // KAN-788: lead attributes validate the remembered attribute filter.
  const leadAttributes = useLeadAttributes();
  const clientFilters = useClientListFilters(resetPage, stagePreset, leadAttributes);
  // KAN-685: a remembered stage/source/account that is no longer offered is dropped.
  useDropStaleFilterValues(clientFilters);
  useEffect(() => {
    // Drop the one-shot preset from the URL so a reload keeps the user's later choice.
    const presetKeys = ['stage', 'funnel', 'source', 'worked', 'outcome', 'manager'];
    if (presetKeys.some((key) => searchParams.has(key))) {
      const next = new URLSearchParams(searchParams);
      presetKeys.forEach((key) => next.delete(key));
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const { applyTo, search: searchQuery, setSearch } = clientFilters;
  const transcript = useClientTranscript();

  // Create form state
  const [createForm, setCreateForm] = useState<CreateClientPayload>({
    phone: '',
    name: '',
    email: '',
    notes: '',
  });
  const [createError, setCreateError] = useState<string | null>(null);

  // Debounce search query
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Set page title
  useEffect(() => {
    document.title = 'Контакты - SMETA AI';
  }, []);

  // Build filters
  const filters: ClientFilters = useMemo(() => {
    const result: ClientFilters = {
      page: currentPage,
      page_size: pageSize,
    };

    if (debouncedSearch) {
      result.search = debouncedSearch;
    }

    return applyTo(result);
  }, [debouncedSearch, currentPage, pageSize, applyTo]);

  // Query clients
  const {
    data: clientsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['clients', filters],
    queryFn: () => clientsApi.list(filters),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (payload: CreateClientPayload) => clientsApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Клиент создан');
      setIsCreateDialogOpen(false);
      setCreateForm({ phone: '', name: '', email: '', notes: '' });
      setCreateError(null);
    },
    onError: (err: Error) => {
      setCreateError(err.message || 'Не удалось создать клиента');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => clientsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Клиент удален');
      setIsDeleteDialogOpen(false);
      setClientToDelete(null);
    },
  });

  // Handlers
  const handleOpenCreateDialog = useCallback(() => {
    setCreateForm({ phone: '', name: '', email: '', notes: '' });
    setCreateError(null);
    setIsCreateDialogOpen(true);
  }, []);

  const handleCloseCreateDialog = useCallback(() => {
    setIsCreateDialogOpen(false);
    setCreateError(null);
  }, []);

  const handleCreateSubmit = useCallback(() => {
    if (!createForm.phone.trim()) {
      setCreateError('Телефон обязателен');
      return;
    }
    createMutation.mutate(createForm);
  }, [createForm, createMutation]);

  const handleDeleteClient = useCallback((client: Client) => {
    setClientToDelete(client);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (clientToDelete) {
      deleteMutation.mutate(clientToDelete.id);
    }
  }, [clientToDelete, deleteMutation]);

  const handleRowClick = useCallback(
    (client: Client) => {
      navigate(`/clients/${client.id}`);
    },
    [navigate]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
    },
    [setSearch]
  );

  return (
    <>
      <PageLayout
        data-testid="clients-page"
        title="Контакты"
        subtitle={<span className="hidden sm:inline">Управление клиентами и их заказами</span>}
        titleIcon={
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
            <Users className="h-5 w-5 text-white" />
          </div>
        }
        titleAddon={
          <span
            data-testid="total-count"
            className="hidden sm:inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold"
          >
            {clientsData?.count ?? 0}
          </span>
        }
        breadcrumbs={[
          { label: 'Главная', href: '/' },
          { label: 'Контакты' },
        ]}
        actions={
          <div className="flex items-center gap-2 sm:gap-4">
            {canCreate && (
              <Button data-testid="add-client-button" onClick={handleOpenCreateDialog}>
                <UserPlus className="h-4 w-4" />
                <span className="hidden sm:inline ml-2">Добавить клиента</span>
              </Button>
            )}
          </div>
        }
      >
        {/* Section tabs (Контакты / Холодные обзвоны) */}
        {tabsBar}

        {/* Search and filters (KAN-782: captions above, controls aligned by the bottom edge) */}
        <div
          data-testid="clients-filters"
          className="mb-4 flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-2"
        >
          <FilterField label="Поиск" className="w-full sm:max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                data-testid="search-input"
                type="text"
                placeholder="Поиск по имени или телефону..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="pl-10"
              />
            </div>
          </FilterField>
          <ClientsFilters
            reply={clientFilters.reply}
            stage={clientFilters.stage}
            sender={clientFilters.sender}
            source={clientFilters.source}
            worked={clientFilters.worked}
            outcome={clientFilters.outcome}
            manager={clientFilters.manager}
            attributeFilter={clientFilters.attributeFilter}
            isActive={clientFilters.isActive}
            onReplyChange={clientFilters.setReply}
            onStageChange={clientFilters.setStage}
            onSenderChange={clientFilters.setSender}
            onSourceChange={clientFilters.setSource}
            onWorkedChange={clientFilters.setWorked}
            onOutcomeChange={clientFilters.setOutcome}
            onManagerChange={clientFilters.setManager}
            onAttributeFilterChange={clientFilters.setAttributeFilter}
            onReset={clientFilters.reset}
          />
        </div>

        {/* Loading State */}
        {isLoading && (
          <div
            data-testid="clients-loading"
            className="flex items-center justify-center py-12"
          >
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        )}

        {/* Error State */}
        {isError && (
          <div
            data-testid="clients-error"
            className="flex flex-col items-center justify-center py-12"
          >
            <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Ошибка загрузки клиентов
            </h3>
            <p className="text-gray-500 mb-4">
              {error instanceof Error ? error.message : 'Произошла ошибка'}
            </p>
            <Button data-testid="retry-button" onClick={() => refetch()}>
              Повторить
            </Button>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !isError && clientsData?.results.length === 0 && (
          <div
            data-testid="clients-empty"
            className="flex flex-col items-center justify-center py-12"
          >
            <Users className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Клиенты не найдены
            </h3>
            <p className="text-gray-500 mb-4">
              Добавьте первого клиента
            </p>
            {canCreate && (
              <Button data-testid="add-client-empty-button" onClick={handleOpenCreateDialog}>
                <UserPlus className="h-4 w-4 mr-2" />
                Добавить клиента
              </Button>
            )}
          </div>
        )}

        {/* Clients Table */}
        {!isLoading && !isError && clientsData && clientsData.results.length > 0 && (
          <>
            {/* Table (desktop) */}
            <div className="hidden md:block bg-white rounded-lg border">
              <Table aria-label="Clients table">
                <TableHeader>
                  <TableRow>
                    <TableHead className={HEAD}>Имя</TableHead>
                    <TableHead className={HEAD}>Телефон</TableHead>
                    <TableHead className={HEAD}>Telegram</TableHead>
                    <TableHead className={HEAD}>Email</TableHead>
                    <TableHead className={`${HEAD} w-px`}>Смет</TableHead>
                    <TableHead className={`${HEAD} w-px`}>Сообщений</TableHead>
                    <TableHead className={HEAD}>Наш аккаунт</TableHead>
                    <TableHead className={HEAD}>Воронка</TableHead>
                    <TableHead className={HEAD}>Менеджер</TableHead>
                    <TableHead className={`${HEAD} w-px`}>Последнее сообщение</TableHead>
                    <TableHead className={`${HEAD} w-px`}>
                      <span className="sr-only">Действия</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clientsData.results.map((client) => (
                    <TableRow
                      key={client.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleRowClick(client)}
                    >
                      <TableCell className={`${CELL} font-medium`}>
                        <div className="flex min-w-0 flex-col items-start gap-0.5">
                          <span
                            className="block max-w-[12rem] truncate"
                            title={client.name || undefined}
                          >
                            {client.name || 'Без имени'}
                          </span>
                          {client.is_own && <OwnContactBadge />}
                          {client.is_identified && (
                            <Badge
                              variant="default"
                              className="whitespace-nowrap px-1.5 py-0 text-[10px] leading-4"
                            >
                              Идентифицирован
                            </Badge>
                          )}
                          <FollowupBadge
                            followupAt={client.followup_at}
                            note={client.followup_note}
                            followupType={client.followup_type}
                            followupTypeLabel={client.followup_type_label}
                            className="block max-w-[12rem] truncate px-1.5 text-[10px]"
                          />
                        </div>
                      </TableCell>
                      <TableCell className={`${CELL} ${NARROW}`}>
                        <div
                          className="flex items-center gap-1.5"
                          data-testid={`client-phone-${client.id}`}
                        >
                          <Phone className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                          {formatClientPhone(client.phone)}
                        </div>
                      </TableCell>
                      <TableCell className={CELL}>
                        {client.telegram_id || client.telegram_username ? (
                          <div className="flex items-center gap-1.5">
                            <MessageCircle className="h-3.5 w-3.5 shrink-0 text-blue-500" />
                            {/* KAN-806: username only, never the numeric id. */}
                            {client.telegram_username ? (
                              <TelegramUsernameLink
                                username={client.telegram_username}
                                className="whitespace-nowrap"
                              />
                            ) : (
                              <span
                                className="whitespace-nowrap text-sm text-gray-400"
                                title="Клиент писал нам в Telegram, username неизвестен"
                                data-testid={`client-telegram-no-username-${client.id}`}
                              >
                                без username
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell className={CELL}>
                        {client.email ? (
                          <div className="flex items-center gap-1.5">
                            <Mail className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                            <span
                              className="block max-w-[10rem] truncate text-sm"
                              title={client.email}
                            >
                              {client.email}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell className={`${CELL} ${NARROW}`}>
                        <div className="flex items-center gap-1.5">
                          <FileText className="h-3.5 w-3.5 text-gray-400" />
                          <span>{client.estimates_count}</span>
                        </div>
                      </TableCell>
                      <TableCell className={`${CELL} ${NARROW}`}>
                        <ClientMessagesCount
                          client={client}
                          testId={`client-messages-count-${client.id}`}
                          transcriptTestId={`open-transcript-${client.id}`}
                          onOpenTranscript={transcript.openFor}
                          loading={transcript.loadingClientId === client.id}
                        />
                      </TableCell>
                      <TableCell className={`${CELL} max-w-[12rem]`}>
                        <ClientSenderAccount
                          client={client}
                          testId={`client-sender-${client.id}`}
                        />
                      </TableCell>
                      <TableCell className={CELL}>
                        <ClientFunnelStage
                          client={client}
                          testId={`client-funnel-${client.id}`}
                        />
                      </TableCell>
                      <TableCell className={`${CELL} max-w-[10rem]`}>
                        <ClientManagerName
                          client={client}
                          testId={`client-manager-${client.id}`}
                        />
                      </TableCell>
                      <TableCell
                        className={`${CELL} ${NARROW} text-sm text-gray-500`}
                        data-testid={`client-date-${client.id}`}
                        title={
                          client.last_message_at
                            ? 'Последнее сообщение'
                            : 'Дата создания клиента'
                        }
                      >
                        {formatClientActivity(client.last_message_at ?? client.created_at)}
                      </TableCell>
                      <TableCell className={`${CELL} ${NARROW}`}>
                        {canDelete && <Button
                          data-testid={`delete-button-${client.id}`}
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClient(client);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-500" />
                        </Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Cards (mobile) */}
            <div className="space-y-3 md:hidden">
              {clientsData.results.map((client) => (
                <div
                  key={client.id}
                  data-testid={`client-card-${client.id}`}
                  className="bg-white border rounded-lg p-4 space-y-3 cursor-pointer"
                  onClick={() => handleRowClick(client)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium text-gray-900 truncate">
                        {client.name || 'Без имени'}
                      </span>
                      {client.is_own && <OwnContactBadge />}
                      {client.is_identified && (
                        <Badge variant="default" className="text-xs shrink-0">
                          Идентифицирован
                        </Badge>
                      )}
                      <FollowupBadge
                        followupAt={client.followup_at}
                        note={client.followup_note}
                        followupType={client.followup_type}
                        followupTypeLabel={client.followup_type_label}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Phone className="h-4 w-4 text-gray-400 shrink-0" />
                    <span className="whitespace-nowrap">{formatClientPhone(client.phone)}</span>
                    <span className="ml-auto whitespace-nowrap text-xs">
                      {formatClientActivity(client.last_message_at ?? client.created_at)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>
                      Сообщений:{' '}
                      <ClientMessagesCount
                        client={client}
                        testId={`client-messages-count-mobile-${client.id}`}
                        transcriptTestId={`open-transcript-mobile-${client.id}`}
                        onOpenTranscript={transcript.openFor}
                        loading={transcript.loadingClientId === client.id}
                      />
                    </span>
                    <ClientFunnelStage
                      client={client}
                      testId={`client-funnel-mobile-${client.id}`}
                    />
                  </div>
                  <div className="flex items-center gap-2 min-w-0 text-sm text-gray-500">
                    <span className="shrink-0">Наш аккаунт:</span>
                    <ClientSenderAccount
                      client={client}
                      testId={`client-sender-mobile-${client.id}`}
                    />
                  </div>
                  {client.manager && (
                    <div className="flex items-center gap-2 min-w-0 text-sm text-gray-500">
                      <span className="shrink-0">Менеджер:</span>
                      <ClientManagerName
                        client={client}
                        testId={`client-manager-mobile-${client.id}`}
                      />
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <FileText className="h-4 w-4 text-gray-400" />
                      <span>Смет: {client.estimates_count}</span>
                    </div>
                    {canDelete && <Button
                      data-testid={`delete-button-mobile-${client.id}`}
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClient(client);
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-500" />
                    </Button>}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {clientsData && (
              <PaginationBar
                page={currentPage}
                pageSize={pageSize}
                count={clientsData.count}
                hasPrevious={Boolean(clientsData.previous)}
                hasNext={Boolean(clientsData.next)}
                onPageChange={setCurrentPage}
                onPageSizeChange={setPageSize}
              />
            )}
          </>
        )}
      </PageLayout>

      {/* Create Client Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent data-testid="create-client-dialog">
          <DialogHeader>
            <DialogTitle>Добавить клиента</DialogTitle>
            <DialogDescription>
              Введите данные нового клиента
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Телефон *</Label>
              <Input
                id="phone"
                data-testid="phone-input"
                placeholder="+7 (999) 123-45-67"
                value={createForm.phone}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, phone: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Имя</Label>
              <Input
                id="name"
                data-testid="name-input"
                placeholder="Иван Иванов"
                value={createForm.name}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                placeholder="ivan@example.com"
                value={createForm.email}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, email: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Заметки</Label>
              <Textarea
                id="notes"
                data-testid="notes-input"
                placeholder="Дополнительная информация о клиенте..."
                value={createForm.notes}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, notes: e.target.value }))
                }
              />
            </div>
            {createError && (
              <div className="text-sm text-red-600">{createError}</div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseCreateDialog}
              data-testid="cancel-button"
            >
              Отмена
            </Button>
            <Button
              onClick={handleCreateSubmit}
              disabled={createMutation.isPending}
              data-testid="submit-button"
            >
              {createMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Создать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent data-testid="confirm-delete-dialog">
          <DialogHeader>
            <DialogTitle>Удаление клиента</DialogTitle>
            <DialogDescription>
              Вы уверены, что хотите удалить клиента{' '}
              {clientToDelete?.name || clientToDelete?.phone}? Это действие
              нельзя отменить.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Отмена
            </Button>
            <Button
              variant="destructive"
              data-testid="confirm-delete-button"
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Удалить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ClientsListView;
